import { ReactNode, useEffect, useState } from "react"
import { candidateTestsPath } from "~/common/paths"
import { Button } from "~/shadcn/ui/button"
import { useSafeMutation } from "~/common/useSafeMutation"
import { gql } from "~/__generated__"
import { useTestFlow } from "./TestFlowProvider"
import { unstable_useBlocker, useNavigate } from "react-router-dom"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/shadcn/ui/dialog"

export const ContinueOrSaveBar = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { candidateTest, isIntentionalExit, setIsIntentionalExit } =
    useTestFlow()
  const [lockCandidateTest] = useSafeMutation(CANDIDATE_TEST_LOCK_MUTATION)
  const [unlockCandidateTest] = useSafeMutation(CANDIDATE_TEST_UNLOCK_MUTATION)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)

  const blocker = unstable_useBlocker(
    ({ currentLocation, nextLocation }) =>
      // Only block if navigating away from test
      !isNavigating && currentLocation.pathname !== nextLocation.pathname
  )

  useEffect(() => {
    if (
      blocker.state === "blocked" &&
      candidateTest?.test.lockAfterSession &&
      !isNavigating
    ) {
      setDialogOpen(true)
    }
  }, [blocker, candidateTest, isNavigating])

  const handleSaveAndExit = async () => {
    if (!candidateTest) return

    setIsNavigating(true)
    if (candidateTest.test.lockAfterSession) {
      setIsIntentionalExit(true)
      await lockCandidateTest({
        variables: {
          input: {
            candidateTestId: candidateTest.id,
          },
        },
      })
    }

    if (blocker.state === "blocked") {
      blocker.proceed()
    }
    setDialogOpen(false)
    navigate(candidateTestsPath.pattern)
    return
  }

  useEffect(() => {
    if (!candidateTest || !candidateTest.test.lockAfterSession) return
    if (isIntentionalExit) return

    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      const message =
        "Once you leave this test, you will have to request to continue from the organization admins."
      event.returnValue = message

      // Set future lock date (45 seconds from now)
      const futureDate = new Date(Date.now() + 45 * 1000)

      try {
        await lockCandidateTest({
          variables: {
            input: {
              candidateTestId: candidateTest.id,
              lockedAt: futureDate.toISOString(),
            },
          },
        })

        console.log("Test lock requested for 45 seconds in the future")
      } catch (error) {
        console.error("Error setting test lock:", error)
      }

      return message
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [candidateTest, isIntentionalExit, lockCandidateTest])

  // Check on page load if test is locked but within 45-second window
  useEffect(() => {
    if (!candidateTest || !candidateTest.test.lockAfterSession) return
    if (!candidateTest.lockedAt) return

    // Check if we're within the 45-second grace period
    const lockedAtDate = new Date(candidateTest.lockedAt)
    const fortyFiveSecondsAgo = new Date(Date.now() - 45 * 1000)

    // If lockedAt is in the future and within 45 seconds
    if (lockedAtDate > fortyFiveSecondsAgo) {
      console.log("Returned within grace period, unlocking test")

      unlockCandidateTest({
        variables: {
          input: {
            candidateTestId: candidateTest.id,
          },
        },
      }).catch((err) => console.error("Failed to unlock test:", err))
    } else {
      console.log("Past grace period, test remains locked")
    }
  }, [candidateTest, unlockCandidateTest])

  return (
    <div className="flex items-center justify-between">
      {children}

      <Dialog
        open={dialogOpen}
        onOpenChange={() => {
          if (dialogOpen) {
            if (blocker.state === "blocked") {
              blocker.reset()
            }
            setDialogOpen(false)
          } else {
            setDialogOpen(true)
          }
        }}
      >
        <DialogTrigger asChild={true}>
          <Button variant="ghost" onClick={() => setDialogOpen(true)}>
            Save as Draft & Exit
          </Button>
        </DialogTrigger>
        <DialogContent className="lg:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium text-gray-333 leading-normal tracking-normal">
              Are you sure?
            </DialogTitle>
          </DialogHeader>
          <p>
            Once you leave this test, you will have to request to continue from
            the organization admins.
          </p>
          <div className="flex items-center gap-2 mt-2">
            <Button
              size="sm"
              onClick={() => {
                if (blocker.state === "blocked") {
                  blocker.reset()
                }
                setDialogOpen(false)
              }}
            >
              Go Back
            </Button>
            <Button size="sm" onClick={handleSaveAndExit}>
              Leave Test
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const CANDIDATE_TEST_LOCK_MUTATION = gql(`
  mutation CandidateTestLock($input: CandidateTestLockInput!) {
    candidateTestLock(input: $input) {
      candidateTest {
        id
      }
    }
  }
`)

const CANDIDATE_TEST_UNLOCK_MUTATION = gql(`
  mutation CandidateTestUnlock($input: CandidateTestUnlockInput!) {
    candidateTestUnlock(input: $input) {
      candidateTest {
        id
      }
    }
  }
`)
